import React, { useMemo } from 'react';
import {
  TECHLAND_CARD_DISPLAY_TEMPLATE_DATA_KEY,
  resolveGameCoverFromGameList,
} from '@home/pages/studio/home/_constant';
import { resolveTechlandShowTimeMetricKey } from '@home/pages/studio/home/_utils';
import { SHOW_TIME_SERVICE_DATA_KEY } from '../ShowTimeService';
import TechlandYtdRevenueCard from './index';
import type { ICardMetricsApiData } from './cardMetricsTypes';
import type { ITechlandYtdRevenueCardProps } from './types';

/**
 * 与 TechlandYtdCardMetricsService 配套：从 CardState 注入的 data 取 Service 已合并的 _cardProps，
 * 加载阶段用 techlandCardDisplayTemplate。不含业务计算，仅桥接数据流与纯展示组件。
 * 副标题结束日优先用 ShowTimeService（_showTimeData）解析的 time，避免 card_metrics 先于 ShowTime 返回时副标题未用接口 time。
 */
const TechlandYtdRevenueCardSlot: React.FC<{
  data?: Record<string, unknown>;
}> = ({ data }) => {
  const fromService = (
    data?.card_data as { data?: ICardMetricsApiData; } | undefined
  )?.data?._cardProps;
  const template = data?.[TECHLAND_CARD_DISPLAY_TEMPLATE_DATA_KEY] as
    | ITechlandYtdRevenueCardProps
    | undefined;
  const cardProps = fromService ?? template;
  const showTimeMap = (
    data?.[SHOW_TIME_SERVICE_DATA_KEY] as
      | { data?: Record<string, string>; }
      | undefined
  )?.data;
  const filterVal = (data as { filter?: Record<string, unknown>; } | undefined)?.filter;

  const studioData = data?.studioData as
    | { game_list?: Array<{ game_code?: string; cover?: string; }>; }
    | undefined;

  const gameCoverIcon = useMemo(
    () => resolveGameCoverFromGameList(studioData, cardProps?.titleIconGameCode),
    [studioData, cardProps?.titleIconGameCode],
  );

  const propsWithSubtitle = useMemo((): ITechlandYtdRevenueCardProps | null => {
    if (
      !cardProps?.budgetGroup ||
      !cardProps?.planGroup ||
      !cardProps?.titleEn ||
      !cardProps?.titleCn
    ) {
      return null;
    }
    const scope = (filterVal as { scope?: string; } | undefined)?.scope;
    const keyFromScopeMap =
      scope && cardProps.subtitleTimeMetricKeyByScope?.[scope];
    const keys = [
      keyFromScopeMap,
      resolveTechlandShowTimeMetricKey(scope),
      cardProps?.subtitleTimeMetricKey,
      cardProps?.actualValueKey,
    ].filter((k): k is string => Boolean(k && String(k).trim() !== ''));

    let endFromShowTime: string | undefined;
    if (showTimeMap) {
      for (const k of keys) {
        const v = showTimeMap[k];
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          endFromShowTime = String(v).trim();
          break;
        }
      }
      if (endFromShowTime === undefined) {
        const vals = Object.values(showTimeMap);
        if (vals.length === 1) {
          const v = vals[0];
          if (v !== undefined && v !== null && String(v).trim() !== '') {
            endFromShowTime = String(v).trim();
          }
        }
      }
    }

    const iconOverride = gameCoverIcon ? { iconSrc: gameCoverIcon } : {};

    if (
      endFromShowTime !== undefined &&
      endFromShowTime !== null &&
      String(endFromShowTime).trim() !== ''
    ) {
      return {
        ...cardProps,
        ...iconOverride,
        subtitleDate: String(endFromShowTime).trim(),
        filterVal,
      };
    }
    return { ...cardProps, ...iconOverride, filterVal };
  }, [cardProps, showTimeMap, filterVal, gameCoverIcon]);

  if (!propsWithSubtitle) {
    return null;
  }
  return <TechlandYtdRevenueCard {...propsWithSubtitle} />;
};

export default TechlandYtdRevenueCardSlot;
